import { useReducer } from 'react';
import { useNavigate, Navigate } from 'react-router';
import { useAuthStore } from '@/stores/authStore';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { PasswordInput } from '@/components/ui/password-input';
import { Card, CardHeader, CardContent, CardFooter } from '@/components/ui/card';
import { LanguageSwitcher } from '@/components/LanguageSwitcher';
import { AuthLogo } from '@/features/auth/AuthLogo';
import { AuthTabs } from '@/features/auth/AuthTabs';
import { useI18n } from '@/i18n/useI18n';

interface RegisterUiState {
  name: string;
  email: string;
  password: string;
  error: string;
  loading: boolean;
}

const initialRegisterUi: RegisterUiState = {
  name: '',
  email: '',
  password: '',
  error: '',
  loading: false,
};

type RegisterUiAction =
  | { type: 'set-name'; value: string }
  | { type: 'set-email'; value: string }
  | { type: 'set-password'; value: string }
  | { type: 'before-submit' }
  | { type: 'submit-done' }
  | { type: 'set-error'; message: string };

function registerUiReducer(state: RegisterUiState, action: RegisterUiAction): RegisterUiState {
  switch (action.type) {
    case 'set-name':
      return { ...state, name: action.value };
    case 'set-email':
      return { ...state, email: action.value };
    case 'set-password':
      return { ...state, password: action.value };
    case 'before-submit':
      return { ...state, error: '', loading: true };
    case 'submit-done':
      return { ...state, loading: false };
    case 'set-error':
      return { ...state, error: action.message };
    default:
      return state;
  }
}

export function RegisterPage() {
  const navigate = useNavigate();
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const [ui, dispatch] = useReducer(registerUiReducer, initialRegisterUi);
  const { t, language } = useI18n();

  // Если пользователь уже авторизован — перенаправляем.
  // Через <Navigate>, а не navigate() в теле рендера: рендер должен оставаться чистым,
  // иначе под StrictMode/конкурентным рендерингом переход в историю уходит дважды.
  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    dispatch({ type: 'before-submit' });
    try {
      const data = (await api.post('/auth/register', {
        name: ui.name,
        email: ui.email,
        password: ui.password,
        // Сервер по ней назовёт личный воркспейс: «Личные проекты» / «Personal Projects».
        // Локаль живёт только на клиенте (localStorage), иначе бэк её не узнает.
        // Шлём только то, что принимает `/auth/register`: добавится третий язык в UI —
        // регистрация не должна начать падать с 400 до фолбэка по Accept-Language.
        language: language === 'ru' || language === 'en' ? language : undefined,
      })) as {
        message?: string;
        verificationExpiresAt?: string;
      };
      void navigate('/verify-email', {
        replace: true,
        state: {
          email: ui.email.trim().toLowerCase(),
          verificationExpiresAt: data.verificationExpiresAt,
        },
      });
    } catch (err) {
      dispatch({
        type: 'set-error',
        message: err instanceof Error ? err.message : t('register.genericError'),
      });
    } finally {
      dispatch({ type: 'submit-done' });
    }
  }

  return (
    <div className="relative flex min-h-[max(100vh,850px)] items-center justify-center bg-muted p-4 pb-20">
      <AuthLogo />
      <Card className="w-full max-w-md gap-8 py-10">
        <CardHeader className="px-12">
          <AuthTabs active="register" />
        </CardHeader>
        <form onSubmit={handleSubmit} className="flex flex-col gap-7">
          <CardContent className="flex flex-col gap-4 px-12">
            {ui.error && (
              <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
                {ui.error}
              </div>
            )}
            <div className="flex flex-col gap-2">
              <label htmlFor="name" className="text-sm font-medium">
                {t('register.name')}
              </label>
              <Input
                id="name"
                type="text"
                placeholder={t('register.namePlaceholder')}
                value={ui.name}
                onChange={(e) => dispatch({ type: 'set-name', value: e.target.value })}
                required
                autoComplete="name"
                autoFocus
              />
            </div>
            <div className="flex flex-col gap-2">
              <label htmlFor="email" className="text-sm font-medium">
                {t('auth.email')}
              </label>
              <Input
                id="email"
                type="email"
                placeholder={t('auth.emailPlaceholder')}
                value={ui.email}
                onChange={(e) => dispatch({ type: 'set-email', value: e.target.value })}
                required
                autoComplete="email"
              />
            </div>
            <div className="flex flex-col gap-2">
              <label htmlFor="password" className="text-sm font-medium">
                {t('register.password')}
              </label>
              <PasswordInput
                id="password"
                placeholder={t('register.passwordPlaceholder')}
                showPasswordLabel={t('password.show')}
                hidePasswordLabel={t('password.hide')}
                value={ui.password}
                onChange={(e) => dispatch({ type: 'set-password', value: e.target.value })}
                required
                minLength={8}
                autoComplete="new-password"
              />
            </div>
          </CardContent>
          <CardFooter className="flex flex-col gap-4 px-12">
            <Button type="submit" className="w-full" disabled={ui.loading}>
              {ui.loading ? t('register.submitting') : t('register.submit')}
            </Button>
            <p className="text-center text-xs text-muted-foreground">
              {t('register.legalPrefix')}{' '}
              <a
                href="/legal/terms-and-conditions.pdf"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block text-primary border-b border-current/15 hover:border-current/40"
              >
                {t('register.legalOfferLink')}
              </a>{' '}
              {t('register.legalAnd')}{' '}
              <a
                href="/legal/privacy-consent.pdf"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block text-primary border-b border-current/15 hover:border-current/40"
              >
                {t('register.legalConsentLink')}
              </a>{' '}
              {t('register.legalConsentSuffix')}
            </p>
          </CardFooter>
        </form>
      </Card>
      <LanguageSwitcher className="absolute bottom-6 left-1/2 -translate-x-1/2 bg-[#f5f5f5]" />
    </div>
  );
}
